<template>
  <div class="fs-block">
    <ChangePreviewItemModal :previewItem="previewItem" :type="currentPreviewType" @cancel="showPreviewChangeModal = false" @select="select" v-if="showPreviewChangeModal" />
    <BeforePreviewModal v-if="showBeforePreviewModal" :type="currentPreviewType" @change="showBeforePreviewModal = false; showPreviewChangeModal = true" @cancel="showBeforePreviewModal = false" :supported="isPreviewSupported" />

    <div :class="{
      'd-xl-block d-lg-block d-md-block d-sm-block': true,
      'iphone-wrapper': true,
      'ipad': ipad
      }" tabindex="-1" :style="{
          left,
          transform: `scale(${scale})`,
          transformOrigin: '0 0',
          marginBottom: `${bottom}px`
        }" ref="fixedIphone">

      <div class="backdrop-close" v-if="ipad" @click="toggleIpadView">
        <div class="close-icon"><IconClose height="45" /></div>
      </div>

      <div class="mb-3 switch-link" role="group" v-if="!dummy && !ipad">
        <button class="btn btn-secondary" @click="showPreviewChangeModal = true">Change preview {{currentPreviewType}}</button>
        <ExternalLink :href="previewLink" @click="handlePreviewModal" class="btn btn-link dark-icon" text="Open preview" />
      </div>

      <div class="iphone">
        <div class="iphone-loading" v-if="iframeLoading">
          <BouncingLoader />
        </div>

        <div class="safari-top">
          <div><span></span> Mobile Preview <span v-if="!previewItem">&mdash; Demo {{currentPreviewType}}</span></div>
        </div>

        <iframe ref="iframe" :src="previewUrl" @load="onReady" @error="resetPointers"/>
      </div>
    </div>
  </div>
</template>

<script>
import { throttle } from 'lodash-es'
import { mapState, mapActions } from 'vuex'

import dom from '@/mixins/dom'
import map from '@/utils/map'
import objToCss from '@/utils/obj-to-css'
import googleFontUrl from '@/utils/googleFontUrl'
import { EventBus } from '@/utils/event-bus'

import ChangePreviewItemModal from '@/components/ChangePreviewItemModal'
import BeforePreviewModal from '@/components/BeforePreviewModal'
import BouncingLoader from '@/components/BouncingLoader'

import '@/assets/scss/_iphone.scss'

export default {
  mixins: [ dom ],
  props: {
    dummy: {
      type: Boolean,
      default: false
    },
    positionRef: HTMLDivElement
  },
  components: {
    ChangePreviewItemModal,
    BouncingLoader,
    BeforePreviewModal
  },
  data: function () {
    return {
      ipad: false,
      iframeLoading: true,
      left: '100%',
      bottom: null,
      scale: 1,
      showPreviewChangeModal: false,
      showBeforePreviewModal: false
    }
  },
  created: function () {
    this.resetPointers()
  },
  mounted: function () {
    EventBus.$on('update-iphone', this.handleIphoneUpdateEvent)

    window.addEventListener('resize', this.updatePosition, {
      capture: true,
      passive: true
    })

    this.$nextTick(this.updatePosition)
  },
  beforeDestroy: function () {
    EventBus.$off('update-iphone', this.handleIphoneUpdateEvent)

    window.removeEventListener('resize', this.updatePosition)

    this.resetPointers()
  },
  computed: {
    ...mapState([ 'db', 'previewItems', 'vFeatures', 'shopObj', 'highlight', 'currentPreviewType', 'amptheme', 'settings', 'picture' ]),
    layout: function () { return this.settings.layout },
    isPreviewSupported: function () {
      if (this.currentPreviewType === 'product') return true
      if (this.currentPreviewType === 'collection' && this.vFeatures.collections) return true
      if (this.currentPreviewType === 'post' && this.vFeatures.articles) return true

      return false
    },
    showPreviewLink: function () {
      if (!this.previewItem) return false

      return this.isPreviewSupported
    },
    previewLink: function () {
      if (!this.showPreviewLink) return

      const link = [
        'https://',
        this.shopObj.domain,
        '/a/sc/amp',
        this.previewItem
      ].join('')

      return `https://preview.superchargify.com/preview?url=${encodeURIComponent(link)}`
    },
    previewItem: function () {
      return this.previewItems[this.currentPreviewType]
    },
    previewUrl: function () {
      const config = [
        this.settings.layout.announcement,
        this.settings.layout.navbar,
        this.settings.layout.search,
        this.settings.layout.product_thumbnails,
        this.settings.layout.product_collection,
        this.settings.layout.product_vendor,
        this.settings.layout.product_review,
        this.settings.layout.product_inventory_status,
        this.settings.layout.product_savings,
        this.settings.layout.product_page_link,
        this.settings.layout.product_grid_review,
        this.settings.layout.product_grid_savings,
        this.settings.layout.product_grid_sold_out_sticker,
        this.settings.layout.superchargify_link,
        this.settings.layout.product_quick_buy,
        this.settings.layout.logo,
        this.settings.layout.filter_button,
        this.settings.layout.savings_perc,
        this.settings.layout.hide_quantity,
        this.settings.layout.recommendations,
        this.settings.layout.tag_group,
        this.settings.layout.related_products,
        this.settings.layout.hide_pricing,
        this.settings.layout.hide_variant_selector,
        this.settings.layout.navbar_position,
        this.settings.layout.product_title,
        this.settings.layout.product_type,
        this.settings.layout.hide_no_review,
        this.settings.layout.product_grid_product_type,
        this.settings.layout.whatsapp,
        this.settings.layout.whatsapp_theme,
        this.settings.layout.product_form_addon,
        this.settings.layout.hide_collection_description
      ].map(c => {
        if (c === true) return '1'
        else if (c === false || c === null || c === undefined) return '0'
        else if (typeof c === 'string') {
          if (c === 'tawk') return 'i'
          return c.charAt(0)
        } else return '1'
      }).join('')

      const query = [
        `domain=${this.shopObj.domain}`,
        `type=${this.currentPreviewType}`,
        `item=${this.previewItem}`,
        `config=${config}`
      ].join('&')

      return `/preview?${query}#development=1`
    }
  },
  methods: {
    handlePreviewModal: function () {
      if (this.showPreviewLink) return

      this.showBeforePreviewModal = true
    },
    updatePosition: throttle(function () {
      if (this.ipad) return

      const elem = this.$refs.fixedIphone
      const rect = this.positionRef.getBoundingClientRect()
      const styles = getComputedStyle(this.positionRef)
      const padding = parseInt(styles.getPropertyValue('padding-left'))

      if (elem) {
        const iphone = elem.getBoundingClientRect()
        const iphoneHeight = elem.offsetHeight
        const iphoneWidth = elem.offsetWidth
        const windowHeight = window.innerHeight

        const space = padding * 1.5
        const bottom = iphone.y + iphoneHeight + padding

        let scaleHeight = 1
        if (bottom > windowHeight) {
          const diff = bottom - windowHeight
          const newHeight = Math.floor(iphoneHeight - diff)

          scaleHeight = newHeight / iphoneHeight
        }

        const newWidth = Math.floor(rect.width - space)
        const scaleWidth = newWidth / iphoneWidth

        this.scale = Math.min(scaleHeight, scaleWidth, 1)
      }

      const left = rect.left + padding
      this.left = `${left}px`
    }, 250),
    handleIphoneUpdateEvent: function () {
      this.applyAllConfig()
    },
    ...mapActions([ 'changePreviewItem' ]),
    select: function (item) {
      this.changePreviewItem({ type: this.currentPreviewType, item })
      this.showPreviewChangeModal = false
    },
    toggleIpadView: function () {
      this.reload()
      this.ipad = !this.ipad
      const body = document.body

      if (this.ipad) {
        this.addClass(body, 'ipad-preview')
      } else {
        this.removeClass(body, 'ipad-preview')
        this.$nextTick(this.updatePosition)
      }
    },
    resetPointers: function () {
      this.reload = function () {}
      this.toggleAmpMenu = function () {}
      this.toggleConsentBar = function () {}
      this.highlightElem = function () {}
      this.addFonts = function () {}
      this.addCss = function () {}
      this.addCustomCss = function () {}
      this.addLanguage = function () {}
    },
    prepareIframe: function () {
      const iframeRef = this.$refs.iframe

      if (!iframeRef) {
        throw new Error('No iframeRef')
      }

      const iframe = iframeRef.contentDocument

      if (!iframe) {
        throw new Error('iframe has no contentDocument')
      }

      if (this.hasClass(iframe.body, 'page-not-found')) {
        this.changePreviewItem({ type: this.currentPreviewType, item: '' })
        throw new Error('iframe is trying to load a 404 page')
      }

      const styleSheets = iframe.styleSheets
      if (!styleSheets) {
        throw new Error('iframe has no styleSheets')
      }

      const lastStyleSheet = styleSheets[styleSheets.length - 1]
      if (!lastStyleSheet) {
        throw new Error('iframe has no lastStyleSheet')
      }

      const cssRules = lastStyleSheet.cssRules
      if (!cssRules) {
        throw new Error('iframe has no lastStyleSheet')
      }

      const ref = this
      const cssIndexMap = {}
      let cssIndex = lastStyleSheet.cssRules.length

      function insertRuleAt (rule, index) {
        try {
          return lastStyleSheet.insertRule(rule, index)
        } catch (e) {
          return null
        }
      }

      function removeRuleFrom (index) {
        try {
          lastStyleSheet.removeRule(index)
        } catch (e) {}
      }

      this.reload = function () {
        try {
          if (!iframe) return
          if (!iframe.location) return
          ref.iframeLoading = true

          if (iframe.location.pathname.startsWith('/api/preview')) {
            iframe.location.reload()
          } else {
            iframe.location = ref.previewUrl
          }
        } catch (e) {}
      }

      this.toggleAmpMenu = function (open = true) {
        try {
          const button = iframe.querySelector('header > .menu-wrapper')
          const openMenu = iframe.querySelector('amp-sidebar[open]')

          if (openMenu && open) return
          if (!openMenu && !open) return

          if (typeof button.click === 'function') button.click()
        } catch (e) {}
      }

      this.toggleConsentBar = function (open = true) {
        try {
          const bar = iframe.querySelector('#cookie-privacy-consent-ui')
          if (open) bar.style.display = 'flex'
          else bar.style.display = ''
        } catch (e) {}
      }

      this.highlightElem = function (newVal, oldVal) {
        const existing = iframe.querySelector('.highlight')
        if (existing) ref.removeClass(existing, 'highlight')

        if (oldVal && oldVal.endsWith('.menu-wrapper')) {
          ref.toggleAmpMenu(false)
        } else if (oldVal && oldVal.endsWith('consent-ui')) {
          ref.toggleConsentBar(false)
        }

        if (!newVal) return
        const newElem = iframe.querySelector(newVal)
        if (!newElem) return

        newElem.scrollIntoView({ behavior: 'smooth', block: 'center' })

        setTimeout((n, ne) => {
          if (n.endsWith('.menu-wrapper')) {
            ref.toggleAmpMenu(true)
          } else if (n.endsWith('consent-ui')) {
            ref.toggleConsentBar(true)
          } else {
            ref.addClass(ne, 'highlight')

            ne.addEventListener('animationend', () => {
              ref.removeClass(ne, 'highlight')
            }, { once: true })
          }
        }, 200, newVal, newElem)
      }

      this.addLanguage = function (key, selector) {
        try {
          const elems = iframe.querySelectorAll(selector)
          const value = ref.settings.language[key]

          if (key === 'logo_asset_version') {
            // console.log('logo.elem.length', elems.length, value)

            if (elems.length === 0) return
            if (elems.length !== 1) {
              setTimeout(() => {
                ref.addLanguage(key, selector)
              }, 150)
              return
            }
          }

          elems.forEach(el => {
            if (key.endsWith('text')) {
              el.textContent = value
            } else if (key.endsWith('placeholder')) {
              el.setAttribute('placeholder', value)
            } else if (key === 'logo_asset_version') {
              let src = ref.picture

              // console.log('logo', src, value)

              if (!src) {
                if (!value) return

                const base = 'https://client-assets.superchargify.com'
                const name = `logo.${ref.settings.language.logo_asset_ext}`
                const prefix = process.env.NODE_ENV === 'development'
                  ? 'dev-'
                  : ''
                src = `${base}/${ref.shopObj.myshopify_domain}/${name}?v=${prefix}${value}`
              }

              const newEl = document.createElement('amp-img')
              newEl.setAttribute('src', src)
              newEl.setAttribute('layout', 'flex-item')

              el.parentNode.replaceChild(newEl, el)
            } else {
              throw new Error('Unknown key in applyLanguage', key)
            }
          })
        } catch (e) {}
      }

      this.addFonts = function (families) {
        try {
          const elem = iframe.getElementById('scgooglefonts')

          if (elem) {
            elem.setAttribute('href', 'https://fonts.googleapis.com/css?family=' + families)
          } else {
            const linkElement = iframe.createElement('link')
            linkElement.setAttribute('id', 'scgooglefonts')
            linkElement.setAttribute('rel', 'stylesheet')
            linkElement.setAttribute('type', 'text/css')
            linkElement.setAttribute('href', 'https://fonts.googleapis.com/css?family=' + families)
            iframe.head.appendChild(linkElement)
          }
        } catch (e) {}
      }

      this.addCss = function (selector, css) {
        if (!cssIndexMap[selector]) {
          if (insertRuleAt(selector, cssIndex) !== null) {
            cssIndex = cssIndex + 1
          }
        } else {
          removeRuleFrom(cssIndexMap[selector])
          insertRuleAt(css, cssIndexMap[selector])
        }
      }

      this.addCustomCss = function () {
        // if (!this.vFeatures) return
        // if (!this.vFeatures.customcss) return
        if (!this.settings.customcss) return
        if (!this.settings.customcss[this.currentPreviewType]) return

        let style = iframe.querySelector('style#superchargify-custom-css')

        if (!style) {
          style = iframe.createElement('style')
          style.setAttribute('id', 'superchargify-custom-css')
          iframe.head.append(style)
        }

        style.innerHTML = this.settings.customcss[this.currentPreviewType]
      }
    },
    onReady: function () {
      try {
        this.prepareIframe()
        this.applyAllConfig()

        setTimeout(() => {
          this.iframeLoading = false
          this.highlightElem(this.highlight)
        }, 300)
      } catch (e) {
        if (e.message.includes('404 page')) return

        setTimeout(this.onReady, 300)
      }
    },
    applyAllConfig: function () {
      this.applyCss()
      this.applyLanguage()
    },
    applyLanguage: function () {
      Object.entries(map.html).forEach(([ key, selector ]) => {
        this.addLanguage(key, selector)
      })
    },
    applyCss: function () {
      const fontContainer = {}

      Object.entries(map.css).forEach(([ category, selector ]) => {
        const css = objToCss(selector, this.amptheme[category], fontContainer)
        if (!css) return

        this.addCss(css, selector)
      })

      this.addCustomCss()

      const families = googleFontUrl(this.amptheme, fontContainer)
      if (families) this.addFonts(families)
    }
  },
  watch: {
    previewUrl: function () {
      this.iframeLoading = true
    },
    highlight: function (newVal, oldVal) {
      this.highlightElem(newVal, oldVal)
    }
  }
}
</script>
